import { initials, toneOf, type Person } from "@/lib/avatars";
import { Avatar as UIAvatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { cn } from "@/lib/utils";

/**
 * A person's picture beside their name (7g): the profile picture they set,
 * or their initials, on shadcn's avatar. Decorative where a name is written
 * beside it, which is everywhere but Account's Profile, so it says nothing to
 * a screen reader unless given an `alt`.
 *
 * The picture shows once it has loaded; until then, and if it fails —
 * taken down since the page was read, or a deployment that stopped keeping
 * pictures — the initials stand in rather than a broken image.
 */

const SIZES = {
  xs: "size-5 text-[0.625rem]",
  sm: "size-6 text-xs",
  md: "size-9 text-sm",
  xl: "size-14 text-xl",
  lg: "size-24 text-3xl",
} as const;

export type AvatarSize = keyof typeof SIZES;

export function Avatar({
  person,
  size = "sm",
  alt = "",
  className = "",
}: {
  /** Null for a deleted account. */
  person: Person | null;
  size?: AvatarSize;
  alt?: string;
  className?: string;
}) {
  const image = person?.image ?? null;
  const tone = person ? toneOf(person.id ?? person.username ?? person.name ?? "") : "bg-muted text-muted-foreground";
  return (
    <UIAvatar
      role={alt ? "img" : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
      // Inline, as a picture beside a name in a sentence is; shadcn's is a block.
      className={cn("inline-flex align-middle", SIZES[size], className)}
    >
      {/* Keyed by the address, so a new picture is tried afresh. */}
      {image && <AvatarImage key={image} src={image} alt="" loading="lazy" decoding="async" />}
      <AvatarFallback className={cn("font-medium leading-none", SIZES[size], tone)}>
        {person ? initials(person.name, person.username) : "?"}
      </AvatarFallback>
    </UIAvatar>
  );
}

/** A space as every wire type that names one sends it. */
export interface SpaceLike {
  /** A notification's space carries its slug alone, which is its id. */
  id?: string;
  slug: string;
  name: string;
  /** The address of its picture, or null for initials. */
  image: string | null;
  isPersonal?: boolean;
}

/**
 * A space's picture beside its name (0034): the one its owner set, or its
 * initials on a colour of its own — a circle, like a person's. A personal
 * space has none of its own: it is its owner's alone, so it shows `owner`,
 * the viewer, as they are shown everywhere else.
 */
export function SpacePicture({
  space,
  owner,
  size = "md",
  alt = "",
  className = "",
}: {
  space: SpaceLike;
  owner?: Person;
  size?: AvatarSize;
  alt?: string;
  className?: string;
}) {
  if (space.isPersonal && owner) return <Avatar person={owner} size={size} alt={alt} className={className} />;
  return (
    <Avatar
      person={{ id: space.id ?? space.slug, name: space.name, image: space.image }}
      size={size}
      alt={alt}
      className={className}
    />
  );
}

/**
 * A few people overlapping, for a team's line: the first `max` of them, as
 * the line names them.
 */
export function AvatarStack({ people, max = 3 }: { people: (Person | null)[]; max?: number }) {
  const shown = people.slice(0, max);
  if (shown.length === 0) return null;
  return (
    <span className="mr-1.5 inline-flex shrink-0 -space-x-1.5 align-middle">
      {shown.map((person, index) => (
        <Avatar key={person?.id ?? index} person={person} size="xs" className="ring-2 ring-card" />
      ))}
    </span>
  );
}
